// The pricing calculator: what a proposed deal would make us, before it is a deal.
//
// The cost of a call is measured, not guessed: it comes from a month of the
// Margin report, over the customers the reader picks (every comparable one by
// default), and any of it can be typed over. The price is a plan — a rate per
// call or per minute, a hosting fee, a prepaid block of minutes or calls, and
// implementation fees — and the partner's shares come from an agreement or
// are typed. From those the page shows the month at full volume, the same deal
// priced the other ways, the margin across volumes, the contract month by
// month, what the deal does to the book's margin, and what the customer saves
// against people.
//
// Every input is in the URL (pricingSearch.ts), which is how a scenario is
// saved: copy the link. The arithmetic is pricingModel.ts; nothing here writes.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { Currency, MarginCustomer, MarginReport } from "@crm/shared";
import styles from "./PricingPage.module.css";
import {
  prepaidCalls,
  bookImpact,
  contractTotals,
  costBasisFrom,
  costSplit,
  DEFAULT_ROUND_UP_MINUTES,
  isComparable,
  prepaidUnitPriceMinor,
  pricingOptions,
  savings,
  steadyState,
  timeline,
  unitFigures,
  usageUnit,
  volumeCurve,
  type Averaging,
  type CostBasis,
  type ImplementationFee,
  type MonthFigures,
  type PricingOption,
  type Scenario,
  type UsagePricing,
} from "./pricingModel.js";
import {
  convertSearch,
  DEFAULTS,
  formatIds,
  formatImplementation,
  searchToInputs,
  withParam,
  type PricingInputs,
  type PricingSearch,
} from "./pricingSearch.js";
import { NumberField, trimNumber } from "./NumberField.js";
import { VolumeCurve } from "./VolumeCurve.js";
import { callsAtPosition, positionOfCalls, SLIDER_STEPS } from "./volumeScale.js";
import { defaultPeriod, shiftPeriod } from "../close/closeSearch.js";
import { Badge, type Tone } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { MonthStepper, SelectionChips } from "../../design/Filters.js";
import { Segmented } from "../../design/Segmented.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { Table, Td, Th } from "../../design/Table.js";
import { MonthlyChart, type ChartSeries } from "../../design/chart/MonthlyChart.js";
import { colorForKey, labelForKey } from "../../design/chart/series.js";
import { usePageChrome } from "../../design/pageChrome.js";
import {
  useAgreementDetails,
  useAvailableAgreements,
  useFxRates,
  useMarginReport,
  usePartnerOptions,
} from "../../api/queries.js";
import {
  addMonths,
  formatIsoMonth,
  formatMonth,
  parseCalendarMonth,
  todayIsoDate,
} from "../../format/calendarDate.js";
import { formatCount, formatMoney, formatPercent, formatRate } from "../../format/money.js";
import { assertNever } from "../../lib/assertNever.js";
import { useDebouncedValue } from "../../lib/useDebouncedValue.js";

const CURRENCIES = [
  { value: "USD", label: "USD" },
  { value: "CAD", label: "CAD" },
] as const satisfies ReadonlyArray<{ value: Currency; label: string }>;

const AVERAGING = [
  { value: "weighted", label: "Pooled", title: "Pool the customers: a busy one counts for more" },
  {
    value: "mean",
    label: "Average",
    title: "Average each customer's own figures: every one counts the same",
  },
] as const satisfies ReadonlyArray<{ value: Averaging; label: string; title: string }>;

const USAGE_OPTIONS = [
  { value: "per_call", label: "Per call" },
  { value: "per_minute", label: "Per min" },
  { value: "per_minute_whole", label: "Per min, rounded up" },
] as const satisfies ReadonlyArray<{ value: UsagePricing; label: string }>;

const ALLOWANCE_UNITS = [
  { value: "minutes", label: "minutes" },
  { value: "calls", label: "calls" },
] as const;

/**
 * The cost basis when nothing is measured and nothing typed: the round figure
 * the team quotes, $0.16 USD a minute, and two-minute calls.
 */
const FALLBACK_BASIS: CostBasis = {
  costPerMinuteMinor: 16,
  minutesPerCall: 2,
  roundUpMinutesPerCall: DEFAULT_ROUND_UP_MINUTES,
};

/** USD to CAD when the FX table is empty: only so the page can draw; the footnote says so. */
const FALLBACK_USD_TO_CAD = 1.37;

type Money = (minor: number) => string;

export function PricingPage(): ReactNode {
  const search = useSearch({ from: "/pricing" });
  const navigate = useNavigate();
  const today = todayIsoDate();

  const fx = useFxRates();
  const latestFx = fx.data?.items[0] ?? null;
  const usdToCad = latestFx?.usd_to_cad ?? FALLBACK_USD_TO_CAD;
  const inputs = useMemo(() => searchToInputs(search, usdToCad), [search, usdToCad]);

  const setSearch = useCallback(
    (next: PricingSearch) => {
      void navigate({ to: "/pricing", search: next, replace: true });
    },
    [navigate],
  );
  const patch = useCallback(
    (changes: Partial<PricingSearch>) => {
      let next: PricingSearch = search;
      for (const [key, value] of Object.entries(changes) as Array<
        [keyof PricingSearch, PricingSearch[keyof PricingSearch]]
      >) {
        next = withParam(next, key, value);
      }
      setSearch(next);
    },
    [search, setSearch],
  );

  const month = inputs.comparablesMonth ?? defaultPeriod(today);
  // Stepping through months asks for the one it stops on.
  const report = useMarginReport(useDebouncedValue(month), inputs.currency);

  usePageChrome({ title: "Pricing calculator" });

  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2_000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <span className={styles.lede}>
          Price a deal against what calls cost last month. Every input is in the link.
        </span>
        <span className={styles.spacer} />
        <Button
          variant="subtle"
          onClick={() => setSearch({})}
          disabled={Object.keys(search).length === 0}
        >
          Start over
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(window.location.href).then(() => setCopied(true));
          }}
        >
          {copied ? "Link copied" : "Copy link"}
        </Button>
        <Segmented
          label="Scenario currency"
          options={CURRENCIES}
          value={inputs.currency}
          onChange={(currency) => setSearch(convertSearch(search, currency, usdToCad))}
        />
      </div>

      <div className={styles.layout}>
        <div className={styles.inputs}>
          <CostCard
            inputs={inputs}
            month={month}
            report={report.data}
            loading={report.isFetching}
            error={report.isError}
            today={today}
            patch={patch}
          />
          <PlanCard inputs={inputs} patch={patch} />
          <SharesCard inputs={inputs} patch={patch} />
          <VolumeCard inputs={inputs} patch={patch} />
          <CustomerCard inputs={inputs} patch={patch} />
        </div>
        <Results inputs={inputs} patch={patch} report={report.data} month={month} today={today} />
      </div>

      <div className={styles.footnote}>
        {latestFx === null
          ? `No FX rate on file; converting at ${formatRate(FALLBACK_USD_TO_CAD)} CAD to the USD.`
          : `1 USD = ${formatRate(latestFx.usd_to_cad)} CAD, as of ${latestFx.effective_date.slice(0, 10)}.`}{" "}
        Amounts are pre-tax, in {inputs.currency}. The margin is on recurring revenue, as on the
        Margin page.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The cost basis
// ---------------------------------------------------------------------------

interface CardProps {
  inputs: PricingInputs;
  patch: (changes: Partial<PricingSearch>) => void;
}

/** The customers the cost is measured from: the chosen ones, or every comparable one. */
function chosenCustomers(
  inputs: PricingInputs,
  report: MarginReport | undefined,
): MarginCustomer[] {
  const comparable = (report?.customers ?? []).filter(isComparable);
  if (inputs.comparableDealIds.length === 0) return comparable;
  return comparable.filter((customer) => inputs.comparableDealIds.includes(customer.deal_id));
}

/** The basis the scenario runs on: measured, typed over field by field, else the fallback. */
function resolveBasis(inputs: PricingInputs, measured: CostBasis | null): CostBasis {
  const base = measured ?? FALLBACK_BASIS;
  return {
    costPerMinuteMinor: inputs.costPerMinuteMinor ?? base.costPerMinuteMinor,
    minutesPerCall: inputs.minutesPerCall ?? base.minutesPerCall,
    roundUpMinutesPerCall: inputs.roundUpMinutesPerCall ?? base.roundUpMinutesPerCall,
  };
}

function CostCard({
  inputs,
  month,
  report,
  loading,
  error,
  today,
  patch,
}: CardProps & {
  month: string;
  report: MarginReport | undefined;
  loading: boolean;
  error: boolean;
  today: string;
}): ReactNode {
  const comparable = (report?.customers ?? []).filter(isComparable);
  const chosen = chosenCustomers(inputs, report);
  const measured = costBasisFrom(chosen, inputs.averaging);
  const split = costSplit(chosen);
  const splitTotal = split.reduce((sum, line) => sum + line.costMinor, 0);
  const selected =
    inputs.comparableDealIds.length === 0
      ? comparable.map((customer) => customer.deal_id)
      : inputs.comparableDealIds;

  const toggle = (dealId: string) => {
    const next = selected.includes(dealId)
      ? selected.filter((id) => id !== dealId)
      : [...selected, dealId];
    // All of them, or none, is the default: every comparable customer.
    const all =
      next.length === 0 || comparable.every((customer) => next.includes(customer.deal_id));
    patch({ from: all ? undefined : formatIds(next) });
  };

  const perMinute = (minor: number) => unitMoney(minor);
  return (
    <Card>
      <CardHeader
        title="What a call costs"
        description="Measured from a month of the Margin report; type over any figure."
        actions={
          <MonthStepper
            label={formatMonth(month)}
            onStep={(delta) => {
              const next = shiftPeriod(month, delta);
              patch({ month: next === defaultPeriod(today) ? undefined : next });
            }}
          />
        }
      />
      <CardBody className={styles.cardStack}>
        {error ? (
          <div className={styles.warn}>
            The margin report did not load; the cost below is the fallback.
          </div>
        ) : comparable.length === 0 ? (
          <div className={styles.muted}>
            {loading
              ? "Loading the month's customers…"
              : `No customer in ${formatMonth(month)} has both a cost and calls to measure.`}
          </div>
        ) : (
          <>
            <SelectionChips
              label="From"
              options={comparable.map((customer) => ({
                value: customer.deal_id,
                label: `${customer.customer_name} · ${perMinute(customer.cost_minor! / customer.volume!.minutes)}/min`,
              }))}
              selected={selected}
              onToggle={toggle}
            />
            <div className={styles.row}>
              <Segmented
                label="How several customers combine"
                variant="inset"
                options={AVERAGING}
                value={inputs.averaging}
                onChange={(avg) => patch({ avg: avg === DEFAULTS.averaging ? undefined : avg })}
              />
              <span className={styles.muted}>
                {chosen.length} customer{chosen.length === 1 ? "" : "s"} ·{" "}
                {formatCount(chosen.reduce((sum, c) => sum + (c.volume?.conversations ?? 0), 0))}{" "}
                calls
              </span>
            </div>
          </>
        )}

        <div className={styles.fieldGrid}>
          <NumberField
            label="Cost per minute"
            prefix="$"
            decimals={4}
            value={inputs.costPerMinuteMinor === null ? undefined : inputs.costPerMinuteMinor / 100}
            placeholder={trimNumber((measured ?? FALLBACK_BASIS).costPerMinuteMinor / 100, 4)}
            hint={measured === null ? "fallback" : "measured"}
            onChange={(value) => patch({ cpm: value })}
          />
          <NumberField
            label="Minutes per call"
            decimals={2}
            value={inputs.minutesPerCall ?? undefined}
            placeholder={trimNumber((measured ?? FALLBACK_BASIS).minutesPerCall, 2)}
            hint={measured === null ? "fallback" : "measured"}
            onChange={(value) => patch({ mpc: value })}
          />
          <NumberField
            label="Rounding up adds"
            suffix="min/call"
            decimals={3}
            max={1}
            value={inputs.roundUpMinutesPerCall ?? undefined}
            placeholder={trimNumber((measured ?? FALLBACK_BASIS).roundUpMinutesPerCall, 3)}
            hint="per call, billed to the whole minute"
            onChange={(value) => patch({ rup: value })}
          />
        </div>

        {splitTotal > 0 && (
          <div>
            <div className={styles.stack} role="img" aria-label="Where the cost goes">
              {split.map((line, index) => (
                <span
                  key={line.key}
                  style={{
                    width: `${(line.costMinor * 100) / splitTotal}%`,
                    background: colorForKey(line.key, index),
                  }}
                  title={`${labelForKey(line.label)} — ${formatPercent((line.costMinor * 100) / splitTotal, 1)}`}
                />
              ))}
            </div>
            <div className={styles.legend}>
              {split.map((line, index) => (
                <span key={line.key}>
                  <i style={{ background: colorForKey(line.key, index) }} />
                  {labelForKey(line.label)} {formatPercent((line.costMinor * 100) / splitTotal, 0)}
                </span>
              ))}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The plan
// ---------------------------------------------------------------------------

function PlanCard({ inputs, patch }: CardProps): ReactNode {
  const unit = usageUnit(inputs.usage) === "minutes" ? "min" : "call";
  const setFees = (fees: ImplementationFee[]) => patch({ impl: formatImplementation(fees) });
  return (
    <Card>
      <CardHeader
        title="How it is priced"
        description="Usage, a hosting fee, a prepaid block of minutes or calls, and implementation."
      />
      <CardBody className={styles.cardStack}>
        <Segmented
          label="Usage pricing"
          options={USAGE_OPTIONS}
          value={inputs.usage}
          onChange={(usage) => patch({ usage: usage === DEFAULTS.usage ? undefined : usage })}
        />
        <div className={styles.fieldGrid}>
          <NumberField
            label={`Price per ${unit}`}
            prefix="$"
            decimals={4}
            value={inputs.rateMinor / 100}
            hint={inputs.prepaidQuantity > 0 ? "past the prepaid block" : undefined}
            onChange={(value) => patch({ rate: value })}
          />
          <NumberField
            label="Hosting fee, monthly"
            prefix="$"
            value={inputs.hostingMinor === 0 ? undefined : inputs.hostingMinor / 100}
            placeholder="0"
            onChange={(value) => patch({ host: value || undefined, saas: undefined })}
          />
        </div>

        <div className={styles.subhead}>Prepaid, monthly</div>
        <div className={styles.fieldGrid}>
          <NumberField
            label="Prepaid"
            decimals={0}
            value={inputs.prepaidQuantity === 0 ? undefined : inputs.prepaidQuantity}
            placeholder="none"
            hint={
              <Segmented
                label="Prepaid unit"
                variant="inset"
                options={ALLOWANCE_UNITS}
                value={inputs.prepaidUnit}
                onChange={(incu) =>
                  patch({ incu: incu === DEFAULTS.includedUnit ? undefined : incu })
                }
              />
            }
            onChange={(value) => patch({ inc: value || undefined })}
          />
          <NumberField
            label="For"
            prefix="$"
            value={inputs.prepaidAmountMinor === 0 ? undefined : inputs.prepaidAmountMinor / 100}
            placeholder="0"
            hint={prepaidHint(inputs)}
            onChange={(value) => patch({ pre: value || undefined })}
          />
        </div>

        <div className={styles.subhead}>Implementation</div>
        {inputs.implementation.length === 0 && (
          <div className={styles.muted}>No implementation fee.</div>
        )}
        {inputs.implementation.map((fee, index) => (
          <div key={index} className={styles.feeRow}>
            <NumberField
              label={`Phase ${index + 1}`}
              prefix="$"
              value={fee.amountMinor / 100}
              onChange={(value) =>
                setFees(
                  inputs.implementation.map((other, i) =>
                    i === index ? { ...other, amountMinor: Math.round((value ?? 0) * 100) } : other,
                  ),
                )
              }
            />
            <NumberField
              label="Billed in month"
              decimals={0}
              min={1}
              max={inputs.termMonths}
              value={fee.month}
              onChange={(value) =>
                setFees(
                  inputs.implementation.map((other, i) =>
                    i === index ? { ...other, month: Math.max(1, Math.round(value ?? 1)) } : other,
                  ),
                )
              }
            />
            <Button
              variant="subtle"
              aria-label={`Remove phase ${index + 1}`}
              onClick={() => setFees(inputs.implementation.filter((_, i) => i !== index))}
            >
              ✕
            </Button>
          </div>
        ))}
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              const last = inputs.implementation.at(-1);
              setFees([
                ...inputs.implementation,
                {
                  amountMinor: last?.amountMinor ?? 4_000_000,
                  month: Math.min(
                    inputs.termMonths,
                    (last?.month ?? 0) + (last === undefined ? 1 : 6),
                  ),
                },
              ]);
            }}
          >
            ＋ Add a phase
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The partner's shares
// ---------------------------------------------------------------------------

function SharesCard({ inputs, patch }: CardProps): ReactNode {
  const partners = usePartnerOptions();
  const agreements = useAvailableAgreements(inputs.partnerId ?? "");
  // The agreement just picked, until its terms arrive and are written into the shares.
  const [pending, setPending] = useState<string | null>(null);
  const [detail] = useAgreementDetails(pending === null ? [] : [pending]);
  const term = detail?.data?.current_term ?? null;

  useEffect(() => {
    if (pending === null || detail?.data === undefined) return;
    patch({
      ag: pending,
      sr: term?.recurring_split_pct,
      si: term?.impl_split_pct,
      sc: term?.cost_split_pct ?? undefined,
    });
    setPending(null);
  }, [pending, detail?.data, term, patch]);

  const agreementName =
    agreements.data?.items.find((agreement) => agreement.id === inputs.agreementId)?.name ?? null;

  return (
    <Card>
      <CardHeader
        title="Whose money it is"
        description="Our share of each kind of money. Sold direct, all of it is ours."
      />
      <CardBody className={styles.cardStack}>
        <div className={styles.fieldGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Partner</span>
            <select
              className={styles.select}
              value={inputs.partnerId ?? ""}
              onChange={(event) =>
                patch({
                  partner: event.target.value || undefined,
                  ag: undefined,
                  sr: undefined,
                  si: undefined,
                  sc: undefined,
                })
              }
            >
              <option value="">Direct (no partner)</option>
              {(partners.data?.items ?? []).map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </label>
          {inputs.partnerId !== null && (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Agreement</span>
              <select
                className={styles.select}
                value={pending ?? inputs.agreementId ?? ""}
                onChange={(event) => {
                  if (event.target.value === "") patch({ ag: undefined });
                  else setPending(event.target.value);
                }}
              >
                <option value="">Type the shares</option>
                {(agreements.data?.items ?? []).map((agreement) => (
                  <option key={agreement.id} value={agreement.id}>
                    {agreement.name}
                    {agreement.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className={styles.fieldGrid}>
          <NumberField
            label="Recurring, ours"
            suffix="%"
            max={100}
            value={inputs.recurringSharePct}
            onChange={(value) => patch({ sr: value, ag: undefined })}
          />
          <NumberField
            label="Implementation, ours"
            suffix="%"
            max={100}
            value={inputs.implementationSharePct}
            onChange={(value) => patch({ si: value, ag: undefined })}
          />
          <NumberField
            label="Cost we carry"
            suffix="%"
            max={100}
            value={inputs.costSharePct}
            hint="metered usage is all ours unless the deal recharges it"
            onChange={(value) => patch({ sc: value, ag: undefined })}
          />
        </div>
        {agreementName !== null && (
          <div className={styles.muted}>Shares from {agreementName}, as in force today.</div>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Volume and the contract
// ---------------------------------------------------------------------------

function VolumeCard({ inputs, patch }: CardProps): ReactNode {
  return (
    <Card>
      <CardHeader
        title="Volume and term"
        description="Full volume, how fast it gets there, and how long the contract runs."
      />
      <CardBody className={styles.cardStack}>
        <div className={styles.slider}>
          <label htmlFor="calls-slider" className={styles.sliderLabel}>
            <span>Calls a month at full volume</span>
            <span className="mono">{formatCount(Math.round(inputs.callsPerMonth))}</span>
          </label>
          <input
            id="calls-slider"
            type="range"
            min={0}
            max={SLIDER_STEPS}
            step={1}
            value={positionOfCalls(inputs.callsPerMonth)}
            aria-valuetext={`${formatCount(Math.round(inputs.callsPerMonth))} calls a month`}
            onChange={(event) => {
              const calls = callsAtPosition(Number(event.target.value));
              patch({ calls: calls === DEFAULTS.calls ? undefined : calls });
            }}
          />
        </div>
        <div className={styles.fieldGrid}>
          <NumberField
            label="Calls a month"
            decimals={0}
            value={inputs.callsPerMonth}
            onChange={(value) => patch({ calls: value })}
          />
          <NumberField
            label="Contract"
            suffix="months"
            decimals={0}
            min={1}
            max={120}
            value={inputs.termMonths}
            onChange={(value) =>
              patch({ term: value === undefined ? undefined : Math.max(1, Math.round(value)) })
            }
          />
          <NumberField
            label="Ramp to full volume"
            suffix="months"
            decimals={0}
            max={60}
            value={inputs.rampMonths === 0 ? undefined : inputs.rampMonths}
            placeholder="none"
            onChange={(value) => patch({ ramp: value || undefined })}
          />
          {inputs.rampMonths > 0 && (
            <NumberField
              label="First month at"
              suffix="%"
              max={100}
              value={inputs.rampStartPct}
              onChange={(value) => patch({ rstart: value })}
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function CustomerCard({ inputs, patch }: CardProps): ReactNode {
  return (
    <Card>
      <CardHeader
        title="Against people, and a target"
        description="What a call costs the customer with a person on it, and the margin to aim for."
      />
      <CardBody className={styles.fieldGrid}>
        <NumberField
          label="Person, per call"
          prefix="$"
          value={inputs.humanCostPerCallMinor / 100}
          hint="the ROI calculator's $6 USD by default"
          onChange={(value) => patch({ human: value })}
        />
        <NumberField
          label="Target margin"
          suffix="%"
          decimals={1}
          max={99.9}
          value={inputs.targetMarginPct ?? undefined}
          placeholder="none"
          hint="adds the price that makes it"
          onChange={(value) => patch({ target: value })}
        />
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

function Results({
  inputs,
  patch,
  report,
  month,
  today,
}: CardProps & {
  report: MarginReport | undefined;
  month: string;
  today: string;
}): ReactNode {
  const measured = costBasisFrom(chosenCustomers(inputs, report), inputs.averaging);
  const scenario: Scenario = {
    cost: resolveBasis(inputs, measured),
    plan: {
      usage: inputs.usage,
      rateMinor: inputs.rateMinor,
      hostingMinor: inputs.hostingMinor,
      prepaid: {
        quantity: inputs.prepaidQuantity,
        unit: inputs.prepaidUnit,
        amountMinor: inputs.prepaidAmountMinor,
      },
      implementation: inputs.implementation,
    },
    shares: {
      recurringPct: inputs.recurringSharePct,
      implementationPct: inputs.implementationSharePct,
      costPct: inputs.costSharePct,
    },
    volume: {
      callsPerMonth: inputs.callsPerMonth,
      rampMonths: inputs.rampMonths,
      rampStartPct: inputs.rampStartPct,
    },
    termMonths: inputs.termMonths,
    humanCostPerCallMinor: inputs.humanCostPerCallMinor,
  };

  const money: Money = (minor) =>
    formatMoney(Math.round(minor), inputs.currency, { omitCurrencyCode: true });
  const unit = (minor: number | null) => (minor === null ? "—" : unitMoney(minor));
  const steady = steadyState(scenario);
  const perUnit = unitFigures(steady);
  const options = pricingOptions(scenario, inputs.targetMarginPct);
  const months = timeline(scenario);
  const totals = contractTotals(months);
  const saved = savings(scenario);
  const firstMonth = inputs.startMonth ?? nextMonth(today);
  const monthLabels = months.map((m) => shiftMonth(firstMonth, m.month - 1));
  const health = healthOf(steady.marginPct, steady.contributionMinor);

  return (
    <div className={styles.results}>
      <Card>
        <CardHeader
          title="A month at full volume"
          description={`${formatCount(Math.round(steady.calls))} calls, ${formatCount(Math.round(steady.minutes))} minutes`}
          actions={<Badge tone={healthTone(health)}>{healthLabel(health)}</Badge>}
        />
        <CardBody className={styles.cardStack}>
          <StatTileRow>
            <StatTile
              label="Billed"
              value={money(steady.recurringGrossMinor)}
              hint={billedBreakdown(steady, money)}
            />
            <StatTile
              label="Our revenue"
              value={money(steady.recurringOursMinor)}
              hint={`${formatPercent(inputs.recurringSharePct, 1)} of billed`}
            />
            <StatTile
              label="Cost we carry"
              value={money(steady.ourCostMinor)}
              hint={`${unit(scenario.cost.costPerMinuteMinor)}/min · ${unit(perUnit.costPerCallMinor)}/call`}
            />
            <StatTile
              label="Contribution"
              value={money(steady.contributionMinor)}
              hint="our revenue less cost we carry"
            />
            <StatTile
              label="Margin"
              value={steady.marginPct === null ? null : formatPercent(steady.marginPct, 1)}
              hint="on our recurring revenue"
            />
          </StatTileRow>
          <dl className={styles.unitGrid}>
            <dt>Billed per call</dt>
            <dd className="mono">{unit(perUnit.billedPerCallMinor)}</dd>
            <dt>Billed per minute</dt>
            <dd className="mono">{unit(perUnit.billedPerMinuteMinor)}</dd>
            <dt>Ours per call</dt>
            <dd className="mono">{unit(perUnit.oursPerCallMinor)}</dd>
            <dt>Contribution per call</dt>
            <dd className="mono">{unit(perUnit.contributionPerCallMinor)}</dd>
          </dl>
        </CardBody>
      </Card>

      <OptionsTable options={options} currency={inputs.currency} unit={unit} />

      <Card>
        <CardHeader
          title="Margin by volume"
          description="The same plan at every volume from none to twice this one."
        />
        <CardBody>
          <VolumeCurve
            points={volumeCurve(scenario)}
            currentCalls={inputs.callsPerMonth}
            prepaidCalls={prepaidCalls(scenario)}
            formatCalls={(calls) => formatCount(Math.round(calls))}
            formatContribution={money}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Over the contract"
          description="Month by month, with the ramp and the implementation fees in the months they bill."
        />
        <CardBody className={styles.cardStack}>
          <StatTileRow>
            <StatTile
              label="Billed"
              value={money(totals.billedMinor)}
              hint={`over ${totals.months} months`}
            />
            <StatTile
              label="Our revenue"
              value={money(totals.oursMinor)}
              hint={`${money(totals.oneTimeOursMinor)} of it implementation`}
            />
            <StatTile label="Cost we carry" value={money(totals.ourCostMinor)} />
            <StatTile label="Contribution" value={money(totals.contributionMinor)} />
            <StatTile
              label="Contract margin"
              value={totals.marginPct === null ? null : formatPercent(totals.marginPct, 1)}
              hint="implementation included"
            />
          </StatTileRow>
          <MonthlyChart
            label="Contribution by contract month"
            months={monthLabels}
            series={contractSeries(months)}
            formatValue={money}
            formatTick={compactMoney}
          />
          <div className={styles.row}>
            <label className={styles.inlineField}>
              <span className={styles.muted}>Contract starts</span>
              <input
                type="month"
                className={styles.select}
                value={firstMonth}
                onChange={(event) => {
                  const value = event.target.value;
                  patch({ start: value === "" || value === nextMonth(today) ? undefined : value });
                }}
              />
            </label>
          </div>
        </CardBody>
      </Card>

      {report !== undefined && (
        <BookCard report={report} month={month} steady={steady} money={money} />
      )}

      <Card>
        <CardHeader
          title="What the customer saves"
          description={`Against ${unit(inputs.humanCostPerCallMinor)} a call handled by a person.`}
        />
        <CardBody className={styles.cardStack}>
          <StatTileRow>
            <StatTile
              label="Saved a month"
              value={money(saved.monthlySavingsMinor)}
              hint={
                saved.savingsPct === null
                  ? `${money(saved.humanMonthlyMinor)} with people`
                  : `${formatPercent(saved.savingsPct, 0)} of ${money(saved.humanMonthlyMinor)} with people`
              }
            />
            <StatTile
              label="Saved, first year"
              value={money(saved.firstYearSavingsMinor)}
              hint="after implementation"
            />
            <StatTile
              label="First-year ROI"
              value={
                saved.firstYearRoiPct === null ? null : formatPercent(saved.firstYearRoiPct, 0)
              }
              hint={`on ${money(saved.firstYearAiMinor)} spent with us`}
            />
            <StatTile
              label="Payback"
              value={
                saved.paybackMonths === null
                  ? "never"
                  : saved.paybackMonths === 0
                    ? "at once"
                    : `${saved.paybackMonths.toFixed(1)} mo`
              }
              hint={
                saved.paybackMonths === null
                  ? "costs more than people"
                  : "to recover implementation"
              }
            />
          </StatTileRow>
          <MonthlyChart
            label="The customer's cost by month, with people and with us"
            months={monthLabels}
            series={savingsSeries(months)}
            formatValue={money}
            formatTick={compactMoney}
          />
        </CardBody>
      </Card>
    </div>
  );
}

function OptionsTable({
  options,
  currency,
  unit,
}: {
  options: readonly PricingOption[];
  currency: Currency;
  unit: (minor: number | null) => string;
}): ReactNode {
  return (
    <Card>
      <CardHeader
        title="The same deal, priced each way"
        description="The plan as entered, the price each other way that keeps its margin, and the target's."
      />
      <Table label="Pricing options">
        <thead>
          <tr>
            <Th>Option</Th>
            <Th numeric>Revenue / call</Th>
            <Th numeric>Cost / call</Th>
            <Th numeric>Contribution</Th>
            <Th numeric>Margin</Th>
          </tr>
        </thead>
        <tbody>
          {options.map((option) => (
            <tr
              key={option.key}
              className={option.kind === "entered" ? styles.enteredRow : undefined}
            >
              <Td>
                <span className="mono">{priceLabel(option.usage, option.rateMinor, currency)}</span>{" "}
                <span className={styles.muted}>{optionNote(option)}</span>
              </Td>
              <Td numeric mono>
                {unit(option.unit.billedPerCallMinor)}
              </Td>
              <Td numeric mono>
                {unit(option.unit.costPerCallMinor)}
              </Td>
              <Td numeric mono>
                {formatMoney(Math.round(option.figures.contributionMinor), currency, {
                  omitCurrencyCode: true,
                })}
              </Td>
              <Td numeric mono>
                {option.figures.marginPct === null
                  ? "—"
                  : formatPercent(option.figures.marginPct, 1)}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function BookCard({
  report,
  month,
  steady,
  money,
}: {
  report: MarginReport;
  month: string;
  steady: ReturnType<typeof steadyState>;
  money: Money;
}): ReactNode {
  const impact = bookImpact(report.totals, steady);
  const moved =
    impact.beforeMarginPct === null || impact.afterMarginPct === null
      ? null
      : impact.afterMarginPct - impact.beforeMarginPct;
  return (
    <Card>
      <CardHeader
        title="The book with this deal"
        description={`${formatMonth(month)}'s book across every customer, with this deal added at full volume.`}
      />
      <CardBody>
        <StatTileRow>
          <StatTile
            label={`Margin, ${formatMonth(month)}`}
            value={
              impact.beforeMarginPct === null ? null : formatPercent(impact.beforeMarginPct, 1)
            }
            hint={`${money(impact.beforeContributionMinor)} contribution`}
          />
          <StatTile
            label="With this deal"
            value={impact.afterMarginPct === null ? null : formatPercent(impact.afterMarginPct, 1)}
            hint={
              moved === null
                ? `${money(impact.afterContributionMinor)} contribution`
                : `${moved >= 0 ? "+" : "−"}${Math.abs(moved).toFixed(1)} pts · ${money(impact.afterContributionMinor)} contribution`
            }
          />
          <StatTile
            label="Share of the book"
            value={
              impact.afterOursMinor <= 0
                ? null
                : formatPercent((steady.recurringOursMinor * 100) / impact.afterOursMinor, 1)
            }
            hint="of our recurring revenue"
          />
        </StatTileRow>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Series and words
// ---------------------------------------------------------------------------

function contractSeries(months: ReturnType<typeof timeline>): ChartSeries[] {
  return [
    {
      key: "contribution",
      label: "Contribution",
      kind: "bar",
      slot: 1,
      values: months.map((m) => m.totalContributionMinor),
    },
    {
      key: "cumulative",
      label: "Running total",
      kind: "line",
      slot: 2,
      values: months.map((m) => m.cumulativeContributionMinor),
    },
  ];
}

function savingsSeries(months: ReturnType<typeof timeline>): ChartSeries[] {
  return [
    {
      key: "human",
      label: "With people",
      kind: "bar",
      slot: 3,
      values: months.map((m) => m.humanCostMinor),
    },
    {
      key: "ai",
      label: "With us",
      kind: "bar",
      slot: 1,
      values: months.map((m) => m.customerBillMinor),
    },
    {
      key: "saved",
      label: "Saved, running total",
      kind: "line",
      slot: 2,
      values: months.map((m) => m.cumulativeSavingsMinor),
    },
  ];
}

function priceLabel(usage: UsagePricing, rateMinor: number, currency: Currency): string {
  const price = unitMoney(rateMinor);
  switch (usage) {
    case "per_call":
      return `${price} per call`;
    case "per_minute":
      return `${price}/min, per second`;
    case "per_minute_whole":
      return `${price}/min, rounded up`;
    default:
      return assertNever(usage, "usage pricing");
  }
}

function optionNote(option: PricingOption): string {
  switch (option.kind) {
    case "entered":
      return "as entered";
    case "equivalent":
      return "same margin";
    case "sameRate":
      return "same rate";
    case "target":
      return "hits the target";
    default:
      return assertNever(option.kind, "pricing option");
  }
}

type Health = "healthy" | "thin" | "breakEven" | "losing" | "unknown";

/** The Margin page's bands, so a deal reads the same on both. */
function healthOf(marginPct: number | null, contributionMinor: number): Health {
  if (marginPct === null) return "unknown";
  if (marginPct >= 50) return "healthy";
  if (marginPct >= 15) return "thin";
  return contributionMinor < 0 ? "losing" : "breakEven";
}

function healthTone(health: Health): Tone {
  switch (health) {
    case "healthy":
      return "good";
    case "thin":
      return "warn";
    case "breakEven":
    case "losing":
      return "bad";
    case "unknown":
      return "neutral";
    default:
      return assertNever(health, "margin health");
  }
}

function healthLabel(health: Health): string {
  switch (health) {
    case "healthy":
      return "Healthy margin";
    case "thin":
      return "Thin margin";
    case "breakEven":
      return "Break-even";
    case "losing":
      return "Losing money";
    case "unknown":
      return "Nothing reaches us";
    default:
      return assertNever(health, "margin health");
  }
}

/** A price per unit: fractions of a cent matter, so up to four places. The page states the currency once. */
function unitMoney(minor: number): string {
  const major = minor / 100;
  const text = trimNumber(Math.abs(major), Math.abs(major) < 10 ? 4 : 2);
  const [whole, fraction = ""] = text.split(".");
  return `${major < 0 ? "−" : ""}$${formatCount(Number(whole))}.${fraction.padEnd(2, "0")}`;
}

/** `$12k`, for an axis. */
function compactMoney(minor: number): string {
  const major = minor / 100;
  const abs = Math.abs(major);
  const sign = major < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}$${trimNumber(abs / 1_000_000, 1)}M`;
  if (abs >= 1_000) return `${sign}$${trimNumber(abs / 1_000, 1)}k`;
  return `${sign}$${trimNumber(abs, 0)}`;
}

function nextMonth(today: string): string {
  return shiftMonth(today.slice(0, 7), 1);
}

function shiftMonth(isoMonth: string, delta: number): string {
  const month = parseCalendarMonth(isoMonth);
  return month === null ? isoMonth : formatIsoMonth(addMonths(month, delta));
}

/** "$2,500 hosting + $1,500 prepaid + $175 usage": what the month's bill is made of. */
function billedBreakdown(month: MonthFigures, money: Money): string {
  const parts = [
    month.hostingMinor > 0 ? `${money(month.hostingMinor)} hosting` : null,
    month.prepaidMinor > 0 ? `${money(month.prepaidMinor)} prepaid` : null,
    month.usageGrossMinor > 0 || (month.hostingMinor === 0 && month.prepaidMinor === 0)
      ? `${money(month.usageGrossMinor)} usage`
      : null,
  ];
  return parts.filter((part) => part !== null).join(" + ");
}

/** "= $1.50/min": what one prepaid unit comes to. Nothing until the block has a price. */
function prepaidHint(inputs: PricingInputs): string | undefined {
  const unitPrice = prepaidUnitPriceMinor({
    quantity: inputs.prepaidQuantity,
    unit: inputs.prepaidUnit,
    amountMinor: inputs.prepaidAmountMinor,
  });
  if (unitPrice === null || inputs.prepaidAmountMinor === 0) return undefined;
  return `= ${unitMoney(unitPrice)}/${inputs.prepaidUnit === "minutes" ? "min" : "call"}`;
}
